import { useAsyncFn } from "react-use";
import { useTranslation } from "react-i18next";
import { Ref, forwardRef, useImperativeHandle, useState } from "react";
import { TransferStatus, TransferService } from "@/domains/transfer";
import { ErrorHelper } from "@/helpers";
import { useMessage } from "@/contexts";
// components
import { Button, Form, Input, Modal, Radio } from "antd";

interface TenantMemberDepositCheckForm {
  status: TransferStatus;
  amount: string;
  result?: string;
  note?: string;
  transaction?: string;
}

interface CheckModalProps {
  onCheck: () => void;
}

export interface CheckModalRef {
  open: (transferId: string) => void;
}

export const CheckModal = forwardRef(
  ({ onCheck }: CheckModalProps, ref: Ref<CheckModalRef>) => {
    const { t } = useTranslation();

    const { messageApi } = useMessage();

    const [showing, setShowing] = useState(false);

    const [transferId, setTransferId] = useState<string>();

    const [form] = Form.useForm<TenantMemberDepositCheckForm>();

    const [disabled, setDisabled] = useState(true);

    const [state, readTransfer] = useAsyncFn(async (transferId: string) => {
      const res = await TransferService.read(transferId).run();
      const transfer = res.data.msg.transfer;
      form.setFieldsValue({
        status: TransferStatus.checked_and_refused,
        amount: transfer.amount,
        result: transfer.result,
        transaction: transfer.transaction?.replaceAll(",", "\n"),
      });
    });

    const handleConfirm = async () => {
      if (!transferId) return;
      const formValue = await form.validateFields();
      try {
        await TransferService.check(transferId, {
          ...formValue,
          // 替代 textarea 的換行符號
          transaction: formValue.transaction
            ? formValue.transaction.replaceAll("\n", ",")
            : undefined,
        }).run();
        setShowing(false);
        onCheck();
      } catch (e) {
        if (ErrorHelper.isAxiosError(e)) {
          void ErrorHelper.handleAxiosErrorMessage(e, messageApi);
        } else {
          console.log(e);
        }
      }
    };

    const handleCancel = () => {
      setShowing(false);
    };

    useImperativeHandle(ref, () => {
      const open = async (_transferId: string) => {
        form.resetFields();
        setTransferId(_transferId);
        await readTransfer(_transferId);
        setShowing(true);
      };

      return { open };
    });
    return (
      <Modal
        title={t("systems.Modal.memberDepositCheck")}
        open={showing}
        footer={[
          <div className="flex jc:end gap:8" key="footer">
            <Button key="back" onClick={handleCancel}>
              {t("systems.Action.cancel")}
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={state.loading}
              onClick={handleConfirm}
            >
              {t("systems.Action.submit")}
            </Button>
          </div>,
        ]}
        onCancel={handleCancel}
      >
        <Form
          form={form}
          labelCol={{ span: 4 }}
          onValuesChange={(_, values) => {
            setDisabled(values.status !== TransferStatus.checked_and_allowed);
          }}
        >
          <Form.Item
            label={t("domains.Transfer.depositStatus")}
            name="status"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio value={TransferStatus.checked_and_allowed}>
                {t("domains.Transfer.checkedAndAllowed")}
              </Radio>
              <Radio value={TransferStatus.checked_and_refused}>
                {t("domains.Transfer.checkedAndRefused")}
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item label={t("domains.Transfer.depositUnit")} name="amount">
            <Input readOnly type="number" />
          </Form.Item>

          <Form.Item
            label={t("domains.Transfer.resultUnit")}
            name="result"
            rules={
              disabled
                ? []
                : [
                    {
                      required: true,
                      message: t("systems.Error.cannotBeEmpty", {
                        field: t("domains.Transfer.resultUnit"),
                      }),
                    },
                  ]
            }
          >
            <Input
              type="number"
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.resultUnit"),
              })}
              disabled={disabled}
            />
          </Form.Item>

          <Form.Item label={t("domains.Transfer.depositNote")} name="note">
            <Input
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.depositNote"),
              })}
              disabled={disabled}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Transfer.transactionHash")}
            name="transaction"
            extra={t("systems.Extra.inputEachOnASeparateLineIfMultiple", {
              field: t("domains.Transfer.transaction"),
            })}
          >
            <Input.TextArea
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.transactionHash"),
              })}
              rows={4}
              disabled={disabled}
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  }
);
